import React, { useEffect, useState } from "react";
import { Accreditations } from "../../components/Accreditations";
import { Guarantees } from "../../components/Guarantees";
import { useLocation, useParams } from "react-router-dom";
import { OrderDetails } from "./OrderDetails/OrderDetails";

import { Loader } from "../../components/ui/Loader";
import { bookingCourseHref } from "../../helpers/bookingNavigation";
import { Steps } from "./Steps/Steps";
import { selectCourse } from "../../redux/slices/coursesSlice";
import { fetchCourses } from "../../redux/thunks/coursesThunk";
import { useDispatch, useSelector } from "react-redux";

export const Booking = () => {
  const courses = useSelector((state) => state.courses.courses);
  const selectedCourse = useSelector((state) => state.courses.selectedCourse);

  const dispatch = useDispatch();

  const { internal_id, p_code } = useParams();
  const { search } = useLocation();
  const matchingCourse = courses.find(
    (course) => String(course.internal_id) === String(internal_id),
  );
  const courseCode = p_code || new URLSearchParams(search).get("pcode") || matchingCourse?.pcode || "DOM-PAK-REA";
  const requestKey = `${internal_id}:${courseCode}`;
  const [loadState, setLoadState] = useState({ key: requestKey, status: "loading" });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    if (matchingCourse) {
      dispatch(selectCourse(internal_id));
      setLoadState({ key: requestKey, status: "ready" });
      return;
    }
    setLoadState({ key: requestKey, status: "loading" });
    // An unrelated cached course must not prevent a cold/deep link from loading.
    dispatch(fetchCourses({ p_code: courseCode }))
      .then((action) => {
        if (!active) return;
        const payload = action.payload;
        const validResponse = action.meta?.requestStatus === "fulfilled" &&
          payload?.success !== false && Array.isArray(payload?.grouped) &&
          payload.grouped.every((group) => Array.isArray(group?.schedules));
        if (!validResponse) {
          setLoadState({ key: requestKey, status: "error" });
          return;
        }
        const found = payload.grouped.some((group) => group.schedules.some(
          (course) => String(course.internal_id) === String(internal_id),
        ));
        setLoadState({ key: requestKey, status: found ? "ready" : "unavailable" });
      })
      .catch(() => {
        if (active) setLoadState({ key: requestKey, status: "error" });
      });
    // Ignore results for a route that the visitor has already left.
    return () => { active = false; };
  }, [courseCode, dispatch, internal_id, matchingCourse, requestKey, retry]);

  const selectedMatchesRoute = matchingCourse && selectedCourse &&
    String(selectedCourse.internal_id) === String(internal_id);
  const status = loadState.key === requestKey ? loadState.status : "loading";
  const retryLoading = () => {
    setLoadState({ key: requestKey, status: "loading" });
    setRetry((value) => value + 1);
  };

  return (
    <div className="wrapper header-fixed bg-styles-top-right">
      <main className="content">
        <section className="section section-40 pt-mob-0 pb-mob-0 bg-white">
          <div className="container container-lg">
            <div className="row g-24 mb-60 mb-mob-30">
              {selectedMatchesRoute ? (
                <>
                  <div className="col-lg-6">
                    <Steps />
                  </div>
                  <OrderDetails course={selectedCourse} />
                </>
              ) : status === "error" ? (
                <div className="col-12" role="alert">
                  <h2 className="mb-12">We couldn’t load your course</h2>
                  <p className="mb-24">Please try again, or return to the course dates to choose your place.</p>
                  <button type="button" className="btn btn--success btn--md radius-5 mr-10" onClick={retryLoading}>Try again</button>
                  <a className="btn btn--outline-secondary btn--md radius-5" href={bookingCourseHref(search, courseCode)}>View course dates</a>
                </div>
              ) : status === "unavailable" ? (
                <div className="col-12" role="status">
                  <h2 className="mb-12">We couldn’t find this course date</h2>
                  <p className="mb-24">This date may no longer be available online. Choose another date to continue.</p>
                  <a className="btn btn--success btn--md radius-5" href={bookingCourseHref(search, courseCode)}>View course dates</a>
                </div>
              ) : (
                <div className="col-12 text-center" role="status" aria-live="polite" aria-busy="true">
                  <div aria-hidden="true"><Loader /></div>
                  <p>Loading your course details…</p>
                </div>
              )}
            </div>
            {selectedMatchesRoute && (
              <div className="d-lg-none">
                <Guarantees row={false} />
              </div>
            )}
            <Accreditations />
          </div>
        </section>
      </main>
    </div>
  );
};
